// AI Tutor streaming presentation (doc 27 §23, §28.5) -- instrumentation.
//
// Development and test only: one record of TIMINGS AND COUNTERS per live
// reply, kept in memory for ST-04's calibration. No reply text, no network
// request, no analytics event. In a production build nothing is exposed and
// nothing is kept.
//
// All times are milliseconds on the presentation clock
// (`revealEnvironment.now`). Backend / model latency is everything up to
// `receivedDoneAt`; frontend presentation latency is
// `firstWordPresentationDelayMs` and `completionLagMs`.
const KEEP = 20

const records = []

function enabled() {
  return process.env.NODE_ENV !== 'production'
}

function span(from, to) {
  return typeof from === 'number' && typeof to === 'number' ? to - from : null
}

/**
 * @param {Object} marks
 * @param {number|null} marks.sendAt            the student's Send
 * @param {number|null} marks.waitStartedAt     the waiting state appeared
 * @param {number|null} marks.firstServerEventAt
 * @param {number|null} marks.firstServerTextAt
 * @param {number|null} marks.firstVisibleAt    the first visible word
 * @param {number|null} marks.receivedDoneAt    the text became complete
 * @param {number|null} marks.visualDoneAt      everything became visible
 * @param {Object} engine  `revealEngine.metrics()`
 * @param {Object} [extra] `{ outcome, atomic, thinkingMsByPhase }`
 */
export function summarizePresentation(marks, engine, extra) {
  const origin = typeof marks.sendAt === 'number' ? marks.sendAt : null
  return {
    ...(extra || {}),
    mode: engine ? engine.mode : null,
    sendAt: origin,
    firstServerEventMs: span(origin, marks.firstServerEventAt),
    firstServerTextMs: span(origin, marks.firstServerTextAt),
    firstVisibleWordMs: span(origin, marks.firstVisibleAt),
    receivedDoneMs: span(origin, marks.receivedDoneAt),
    visualDoneMs: span(origin, marks.visualDoneAt),
    firstWordPresentationDelayMs: span(
      marks.firstServerTextAt,
      marks.firstVisibleAt
    ),
    completionLagMs: span(marks.receivedDoneAt, marks.visualDoneAt),
    thinkingMs: span(marks.waitStartedAt, marks.firstVisibleAt),
    maxBacklog: engine ? engine.maxBacklog : 0,
    averageBacklog: engine ? Math.round(engine.averageBacklog * 10) / 10 : 0,
    maxPresentationLagMs: engine ? engine.maxLagMs : 0,
    catchUpEntries: engine ? engine.catchUpEntries : 0,
    snaps: engine ? engine.snaps : 0,
    receivedUnits: engine ? engine.receivedUnits : 0,
  }
}

/** Keeps the summary of one reply (newest last). */
export function recordPresentation(summary) {
  if (!enabled()) return
  records.push(summary)
  while (records.length > KEEP) records.shift()
  if (typeof window !== 'undefined') {
    window.__bornaTutorPresentation = {
      last: summary,
      history: records.slice(),
    }
  }
}

export function presentationRecords() {
  return records.slice()
}

export function clearPresentationRecords() {
  records.length = 0
  if (typeof window !== 'undefined') delete window.__bornaTutorPresentation
}
